import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

/**
 * Pass Only Wizard
 * 
 * A streamlined 3-step wizard for users who just want a wallet pass.
 * Much simpler than the full generator - focuses only on what's needed for a pass.
 */

// Step indicator component
const StepIndicator: React.FC<{ currentStep: number; totalSteps: number }> = ({ 
  currentStep, 
  totalSteps 
}) => {
  return (
    <div className="flex items-center justify-center gap-2 mt-8">
      {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
        <React.Fragment key={step}>
          <div
            className={`
              w-3 h-3 rounded-full transition-all duration-300
              ${step === currentStep 
                ? 'bg-rose-500 scale-125' 
                : step < currentStep 
                  ? 'bg-rose-300' 
                  : 'bg-gray-200'
              }
            `}
          />
          {step < totalSteps && (
            <div 
              className={`w-8 h-0.5 ${step < currentStep ? 'bg-rose-300' : 'bg-gray-200'}`} 
            />
          )}
        </React.Fragment>
      ))}
      <div className="ml-4 text-sm text-gray-500">
        Step {currentStep} of {totalSteps}
      </div>
    </div>
  );
};

// Form data interface
interface PassFormData {
  // Step 1: Basic Info
  name: string;
  title: string;
  company: string;
  email: string;
  phone: string;
  website: string;
  
  // Step 2: Customization
  color: string;
  logoUrl: string;
  includeQr: boolean;
  includePhone: boolean;
  includeEmail: boolean;
  includeWebsite: boolean;
}

const initialFormData: PassFormData = {
  name: '',
  title: '',
  company: '',
  email: '',
  phone: '',
  website: '',
  color: '#3B82F6',
  logoUrl: '',
  includeQr: true,
  includePhone: true,
  includeEmail: true,
  includeWebsite: false
};

// Color presets
const colorPresets = [
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Rose', value: '#E11D48' },
  { name: 'Emerald', value: '#10B981' },
  { name: 'Purple', value: '#8B5CF6' },
  { name: 'Orange', value: '#F97316' },
  { name: 'Slate', value: '#475569' },
  { name: 'Black', value: '#1F2937' },
  { name: 'Teal', value: '#14B8A6' }
];

/**
 * Step 1: Your Information
 */
interface Step1Props {
  data: PassFormData;
  onChange: (data: Partial<PassFormData>) => void;
  onNext: () => void;
}

const Step1Information: React.FC<Step1Props> = ({ data, onChange, onNext }) => {
  const isValid = data.name.trim() !== '' && data.email.trim() !== '';
  
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="w-full max-w-lg mx-auto"
    >
      <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">
        Create Your Wallet Pass
      </h2>
      <p className="text-gray-600 mb-8 text-center">
        Enter your contact information
      </p>
      
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name <span className="text-rose-500">*</span>
          </label>
          <input
            type="text"
            value={data.name}
            onChange={(e) => onChange({ name: e.target.value })}
            placeholder="John Smith"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-colors"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title
          </label>
          <input
            type="text"
            value={data.title}
            onChange={(e) => onChange({ title: e.target.value })}
            placeholder="Software Engineer"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-colors"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Company
          </label>
          <input
            type="text"
            value={data.company}
            onChange={(e) => onChange({ company: e.target.value })}
            placeholder="Acme Inc"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-colors"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email <span className="text-rose-500">*</span>
          </label>
          <input
            type="email"
            value={data.email}
            onChange={(e) => onChange({ email: e.target.value })}
            placeholder="john@acme.com"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-colors"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Phone
          </label>
          <input
            type="tel"
            value={data.phone}
            onChange={(e) => onChange({ phone: e.target.value })}
            placeholder="(555) 123-4567"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-colors"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Website
          </label>
          <input
            type="url"
            value={data.website}
            onChange={(e) => onChange({ website: e.target.value })}
            placeholder="www.acme.com"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-colors"
          />
        </div>
      </div>
      
      <div className="mt-6 flex justify-end">
        <button
          onClick={onNext}
          disabled={!isValid}
          className={`
            px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2
            ${isValid 
              ? 'bg-rose-500 hover:bg-rose-600 text-white shadow-md hover:shadow-lg' 
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          Continue
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </motion.div>
  );
};

/**
 * Step 2: Customize
 */
interface Step2Props {
  data: PassFormData;
  onChange: (data: Partial<PassFormData>) => void;
  onBack: () => void;
  onNext: () => void;
}

const Step2Customize: React.FC<Step2Props> = ({ data, onChange, onBack, onNext }) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="w-full max-w-4xl mx-auto"
    >
      <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">
        Customize Your Pass
      </h2>
      <p className="text-gray-600 mb-8 text-center">
        Choose your style and what to include
      </p>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Options */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-6">
          {/* Color Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Pass Color
            </label>
            <div className="flex flex-wrap gap-2">
              {colorPresets.map((preset) => (
                <button
                  key={preset.value}
                  onClick={() => onChange({ color: preset.value })}
                  className={`
                    w-10 h-10 rounded-lg transition-all
                    ${data.color === preset.value 
                      ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' 
                      : 'hover:scale-105'
                    }
                  `}
                  style={{ backgroundColor: preset.value }}
                  title={preset.name}
                />
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <span className="text-sm text-gray-500">Custom:</span>
              <input
                type="color"
                value={data.color}
                onChange={(e) => onChange({ color: e.target.value })}
                className="w-8 h-8 rounded cursor-pointer"
              />
              <span className="text-sm text-gray-600 font-mono">{data.color}</span>
            </div>
          </div>
          
          {/* Logo Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Logo (optional)
            </label>
            <div className="flex items-center gap-4">
              {data.logoUrl ? (
                <div className="relative">
                  <img 
                    src={data.logoUrl} 
                    alt="Logo" 
                    className="w-16 h-16 object-contain rounded-lg border border-gray-200"
                  />
                  <button
                    onClick={() => onChange({ logoUrl: '' })}
                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <label className="flex items-center justify-center w-16 h-16 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-rose-400 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                          onChange({ logoUrl: ev.target?.result as string });
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </label>
              )}
              <span className="text-sm text-gray-500">Upload your logo</span>
            </div>
          </div>
          
          {/* Include Options */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Include on Pass
            </label>
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={data.includeQr}
                  onChange={(e) => onChange({ includeQr: e.target.checked })}
                  className="w-5 h-5 text-rose-500 rounded focus:ring-rose-500"
                />
                <span className="text-gray-700">QR Code (for sharing)</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={data.includePhone}
                  onChange={(e) => onChange({ includePhone: e.target.checked })}
                  className="w-5 h-5 text-rose-500 rounded focus:ring-rose-500"
                />
                <span className="text-gray-700">Phone number</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={data.includeEmail}
                  onChange={(e) => onChange({ includeEmail: e.target.checked })}
                  className="w-5 h-5 text-rose-500 rounded focus:ring-rose-500"
                />
                <span className="text-gray-700">Email address</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={data.includeWebsite}
                  onChange={(e) => onChange({ includeWebsite: e.target.checked })}
                  className="w-5 h-5 text-rose-500 rounded focus:ring-rose-500"
                />
                <span className="text-gray-700">Website</span>
              </label>
            </div>
          </div>
        </div>
        
        {/* Preview */}
        <div className="bg-gray-50 rounded-2xl p-6">
          <h3 className="text-sm font-medium text-gray-500 mb-4 text-center">PREVIEW</h3>
          <div className="flex justify-center">
            <WalletPassPreview data={data} />
          </div>
        </div>
      </div>
      
      <div className="mt-6 flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-semibold text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <button
          onClick={onNext}
          className="px-6 py-3 rounded-lg font-semibold bg-rose-500 hover:bg-rose-600 text-white shadow-md hover:shadow-lg transition-all flex items-center gap-2"
        >
          Continue
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </motion.div>
  );
};

/**
 * Wallet Pass Preview Component
 */
const WalletPassPreview: React.FC<{ data: PassFormData }> = ({ data }) => {
  // Calculate text color based on background
  const getContrastColor = (hexColor: string) => {
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#1F2937' : '#FFFFFF';
  };
  
  const textColor = getContrastColor(data.color);
  const secondaryColor = textColor === '#FFFFFF' ? 'rgba(255,255,255,0.7)' : 'rgba(31,41,55,0.6)';
  
  return (
    <div 
      className="w-64 rounded-2xl shadow-xl overflow-hidden"
      style={{ backgroundColor: data.color }}
    >
      {/* Header */}
      <div className="p-4 flex items-center gap-3">
        {data.logoUrl ? (
          <img src={data.logoUrl} alt="Logo" className="w-10 h-10 rounded-lg object-contain bg-white" />
        ) : (
          <div 
            className="w-10 h-10 rounded-lg flex items-center justify-center text-lg font-bold"
            style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: textColor }}
          >
            {data.company ? data.company.charAt(0).toUpperCase() : data.name ? data.name.charAt(0).toUpperCase() : 'S'}
          </div>
        )}
        <div>
          <div className="text-xs font-medium" style={{ color: secondaryColor }}>
            {data.company || 'Digital Business Card'}
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="px-4 pb-2">
        <div className="text-xl font-bold" style={{ color: textColor }}>
          {data.name || 'Your Name'}
        </div>
        {data.title && (
          <div className="text-sm mt-1" style={{ color: secondaryColor }}>
            {data.title}
          </div>
        )}
      </div>
      
      {/* Fields */}
      <div className="px-4 py-3 space-y-2">
        {data.includePhone && data.phone && (
          <div className="flex items-center gap-2 text-sm" style={{ color: textColor }}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            {data.phone}
          </div>
        )}
        {data.includeEmail && data.email && (
          <div className="flex items-center gap-2 text-sm" style={{ color: textColor }}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span className="truncate">{data.email}</span>
          </div>
        )}
        {data.includeWebsite && data.website && (
          <div className="flex items-center gap-2 text-sm" style={{ color: textColor }}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
            </svg>
            <span className="truncate">{data.website}</span>
          </div>
        )}
      </div>
      
      {/* QR Code Placeholder */}
      {data.includeQr && (
        <div className="p-4 flex justify-center">
          <div 
            className="w-24 h-24 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: 'rgba(255,255,255,0.9)' }}
          >
            <svg className="w-16 h-16 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 3h6v6H3V3zm2 2v2h2V5H5zm8-2h6v6h-6V3zm2 2v2h2V5h-2zM3 13h6v6H3v-6zm2 2v2h2v-2H5zm13-2h1v1h-1v-1zm-3 0h1v1h-1v-1zm-1 1h1v1h-1v-1zm2 0h1v1h-1v-1zm1 1h1v1h-1v-1zm-3 0h1v1h-1v-1zm4 0h1v1h-1v-1zm0 2h1v1h-1v-1zm-4 0h1v1h-1v-1zm2 0h1v1h-1v-1zm2 1h1v1h-1v-1zm-2 0h1v1h-1v-1z"/>
            </svg>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Step 3: Get Your Pass
 */
interface Step3Props {
  data: PassFormData;
  onBack: () => void;
  onComplete: () => void;
  isLoading?: boolean;
}

const Step3Complete: React.FC<Step3Props> = ({ data, onBack, onComplete, isLoading }) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="w-full max-w-lg mx-auto"
    >
      <div className="text-center mb-8">
        <div className="text-5xl mb-4">🎉</div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Your Pass is Ready!
        </h2>
        <p className="text-gray-600">
          Add it to your phone and start sharing
        </p>
      </div>
      
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        {/* Preview */}
        <div className="flex justify-center mb-6">
          <WalletPassPreview data={data} />
        </div>
        
        {/* Add to Wallet Buttons */}
        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-700 text-center mb-4">
            Add to your phone:
          </p>
          
          <button
            onClick={onComplete}
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-black text-white rounded-xl font-semibold hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            Add to Apple Wallet
          </button>
          
          <button
            onClick={onComplete}
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-white border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Add to Google Wallet
          </button>
        </div>
        
        {/* Share Options */}
        <div className="mt-6 pt-6 border-t border-gray-100">
          <p className="text-sm font-medium text-gray-700 text-center mb-4">
            Or share:
          </p>
          <div className="flex justify-center gap-3">
            <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              Copy Link
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Email
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3 3h6v6H3V3zm2 2v2h2V5H5zm8-2h6v6h-6V3zm2 2v2h2V5h-2zM3 13h6v6H3v-6zm2 2v2h2v-2H5z"/>
              </svg>
              QR Code
            </button>
          </div>
        </div>
      </div>
      
      <div className="mt-6 flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-semibold text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
      </div>
      
      {/* Upsell */}
      <div className="mt-8 p-4 bg-gradient-to-r from-rose-50 to-purple-50 rounded-xl border border-rose-100">
        <div className="flex items-start gap-3">
          <div className="text-2xl">💡</div>
          <div>
            <p className="font-semibold text-gray-900">Want email signatures and digital cards too?</p>
            <p className="text-sm text-gray-600 mt-1">
              Upgrade to the Full Brand Kit for a complete professional identity.
            </p>
            <button className="mt-3 text-sm font-semibold text-rose-600 hover:text-rose-700 flex items-center gap-1">
              Upgrade to Full Brand Kit
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/**
 * Main Pass Only Wizard Component
 */
export const PassOnlyWizard: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<PassFormData>(initialFormData);
  const [isLoading, setIsLoading] = useState(false);
  
  const updateFormData = (updates: Partial<PassFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };
  
  const handleComplete = async () => {
    setIsLoading(true);
    
    // TODO: Integrate with actual wallet pass generation
    // For now, navigate to the digital card page with the data
    console.log('[PassOnlyWizard] Creating pass with data:', formData);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setIsLoading(false);
    
    // Navigate to digital card page
    navigate('/digital-card');
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4">
      {/* Back to Dashboard */}
      <div className="max-w-4xl mx-auto mb-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Dashboard
        </button>
      </div>
      
      {/* Wizard Content */}
      <AnimatePresence mode="wait">
        {step === 1 && (
          <Step1Information
            key="step1"
            data={formData}
            onChange={updateFormData}
            onNext={() => setStep(2)}
          />
        )}
        {step === 2 && (
          <Step2Customize
            key="step2"
            data={formData}
            onChange={updateFormData}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
          />
        )}
        {step === 3 && (
          <Step3Complete
            key="step3"
            data={formData}
            onBack={() => setStep(2)}
            onComplete={handleComplete}
            isLoading={isLoading}
          />
        )}
      </AnimatePresence>
      
      {/* Step Indicator */}
      <StepIndicator currentStep={step} totalSteps={3} />
    </div>
  );
};

export default PassOnlyWizard;
